/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel } from '../../models';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { Observable, Subscriber } from 'rxjs';
import { StripeAddressElementOptions, StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';

declare let Stripe: any;

@Injectable()
export class StripeService {
    /**
     * Stripe element options.
     * @private
     */
    private elementsOptions: StripeElementsOptions = {
        locale: 'en',
        // passing the client secret obtained from the server
        clientSecret: '{{CLIENT_SECRET}}',
        appearance: {
            theme: 'stripe',
            variables: {
                fontSizeBase: '14px',
                borderRadius: '4px',
                colorDanger: '#f41171',
                colorPrimary: '#54009c',
                colorSuccess: '#00b391',
                colorText: '#333',
                colorWarning: '#ffa340',
                fontFamily: '"Roboto", sans-serif',
            },
            rules: {
                '.Label': {
                    fontWeight: '600',
                    fontFamily: '"Roboto", sans-serif',
                    letterSpacing: '0.02em',
                    marginBottom: '6px'
                },
                '.Input, .CheckboxInput, .CodeInput': {
                    transition: 'none',
                },
                '.Input': {
                    padding: '8px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #b2b2b2',
                    fontFamily: '"Roboto", sans-serif',
                },
                '.Input::placeholder': {
                    fontSize: '14px',
                    letterSpacing: '0.02em',
                },
                '.Input--invalid': {
                    color: '#f41171'
                },
                '.Tab, .Block, .PickerItem--selected': {
                    backgroundColor: '#fff',
                },
                '.Tab': {
                    transition: 'none'
                },
                '.Tab:hover': {
                    backgroundColor: '#eee'
                },
                '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
                    color: 'var(--colorText)',
                    backgroundColor: '#fff'
                },
                '.Tab:focus, .Tab--selected:focus': {
                    boxShadow: 'inset -1px -1px #0a0a0a, inset 1px 1px #ffffff, inset -2px -2px #808080, inset 2px 2px #dfdfdf',
                    outline: 'none'
                },
                '.Tab:focus-visible': {
                    outline: 'var(--focusOutline)'
                },
                '.PickerItem': {
                    backgroundColor: '#dfdfdf',
                    boxShadow: 'inset -1px -1px #0a0a0a, inset 1px 1px #ffffff, inset -2px -2px #808080, inset 2px 2px #dfdfdf',
                    transition: 'none'
                },
                '.PickerItem:hover': {
                    backgroundColor: '#eee'
                },
                '.PickerItem--highlight': {
                    outline: '1px solid blue'
                },
                '.PickerItem--selected:hover': {
                    backgroundColor: '#dfdfdf'
                }
            }
        }
    };

    /**
     * Billing address options
     * @private
     */
    private billingAddressOptions: StripeAddressElementOptions = {
        mode: 'billing'
    };

    /**
     * Payment options
     * @private
     */
    private paymentElementOptions: StripePaymentElementOptions = {
        layout: {
            type: 'tabs',
            defaultCollapsed: false,
        }
    };

    /**
     * Store the stripe object
     * @private
     */
    private stripe: any;

    /**
     * Store the stripe elements
     * @private
     */
    private elements: any;

    constructor(
        private logService: LogService,
    ) {
    }

    /**
     * Initialise stripe with the public key.
     * @param key
     */
    public initialiseStripe(key: any): void {
        this.stripe = Stripe(key);
    }

    /**
     * Set the element options.
     * @param clientSecret
     */
    public setUpElementOptions(clientSecret: any): void {
        this.elementsOptions.clientSecret = clientSecret;

        this.elements = this.stripe.elements(this.elementsOptions);
    }

    /**
     * Create address element for view.
     */
    public createAddressElement(): any {
        const options: StripeAddressElementOptions = this.billingAddressOptions;

        return this.elements.create('address', options);
    }

    /**
     * Create payment element for the view.
     */
    public createPaymentElement(): any {
        const paymentElementOptions: StripePaymentElementOptions = this.paymentElementOptions;

        return this.elements.create('payment', paymentElementOptions);
    }

    /**
     * Submit payment (create a observable function).
     */
    public submitPaymentMethod(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.stripe.confirmSetup({ elements: this.elements, redirect: 'if_required' })
                .then((result: any): void => {
                        this.logService.debug('StripeService:submitPaymentMethod', result);
                        if (result.error) {
                            observer.error(result.error);
                            observer.complete();
                        } else {
                            if (result.setupIntent.status === 'succeeded') {
                                observer.next(result.setupIntent);
                                observer.complete();
                            }
                        }
                    }
                )
                .catch((error: ErrorResponseModel): void => {
                        this.logService.error('StripeService:submitPaymentMethod', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Get unit price.
     * @param amount
     */
    public getUnitPrice(amount: number): number {
        return amount / 100;
    }

    /**
     * Get currency symbol.
     * @param currency
     */
    public getCurrencySymbol(currency: string): string {
        let symbol: string;
        switch (currency) {
            case 'usd':
                symbol = '$';
                break;
            case 'gbp':
                symbol = '£';
                break;
            default:
                symbol = '';
        }

        return symbol;
    }
}
